import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import Pouch from "@/components/Pouch";

export default function Checkout() {
  const { items, subtotal, discount, discountPct, shipping, total, clear, promoCode } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [razorpayEnabled, setRazorpayEnabled] = useState(false);
  const [upiId, setUpiId] = useState("9481091150@upi");
  const [businessName, setBusinessName] = useState("Rudra Roast Coffee");
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    pincode: "",
    payment_method: "upi",
    upi_reference: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => {
      setRazorpayEnabled(r.data.razorpay_enabled);
      if (r.data.upi_id) setUpiId(r.data.upi_id);
      if (r.data.business_name) setBusinessName(r.data.business_name);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, customer_name: f.customer_name || user.name, email: f.email || user.email }));
  }, [user]);

  useEffect(() => {
    // Preload Razorpay script
    if (!document.getElementById("razorpay-checkout-script")) {
      const s = document.createElement("script");
      s.id = "razorpay-checkout-script";
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (items.length === 0) { setError("Cart is empty"); return; }
    if (form.payment_method === "upi" && !form.upi_reference.trim()) {
      setError("Please enter the UPI transaction reference after completing the payment.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        items,
        promo_code: promoCode || "",
        ...form,
      });

      if (form.payment_method === "razorpay") {
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: data.razorpay_order.amount,
          currency: data.razorpay_order.currency,
          order_id: data.razorpay_order.id,
          name: "Rudra Roast Coffee",
          description: `Order ${data.order.id}`,
          prefill: { name: form.customer_name, email: form.email, contact: form.phone },
          theme: { color: "#E5383F" },
          handler: async (resp) => {
            try {
              await axios.post(`${API}/orders/verify`, {
                order_id: data.order.id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              clear();
              nav(`/order-success/${data.order.id}`);
            } catch (err) {
              setError("Payment verification failed. Please contact support.");
              setSubmitting(false);
            }
          },
          modal: { ondismiss: () => setSubmitting(false) },
        });
        rzp.open();
      } else {
        clear();
        nav(`/order-success/${data.order.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to place order");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[720px] mx-auto px-6 pt-40 text-center">
        <div className="label-mono mb-4">Checkout</div>
        <h1 className="font-serif text-5xl">Nothing to check out.</h1>
        <p className="mt-4 text-[var(--text-secondary)]">Add a blend or build your own first.</p>
        <button onClick={() => nav("/shop")} className="btn-primary mt-8">Browse Blends</button>
      </div>
    );
  }

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-32 pb-24">
      <div className="label-mono mb-4">Checkout</div>
      <h1 className="font-serif text-5xl md:text-6xl leading-none tracking-tighter mb-14">Almost yours.</h1>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-8" data-testid="checkout-form">
        <div className="md:col-span-7 space-y-8">
          <Section title="01 · Delivery Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name" required v={form.customer_name} onChange={set("customer_name")} tid="checkout-name" />
              <Field label="Email" type="email" required v={form.email} onChange={set("email")} tid="checkout-email" />
              <Field label="Phone" required v={form.phone} onChange={set("phone")} tid="checkout-phone" />
              <Field label="Pincode" required v={form.pincode} onChange={set("pincode")} tid="checkout-pincode" />
            </div>
            <Field label="Address" required v={form.address} onChange={set("address")} tid="checkout-address" />
            <Field label="City" required v={form.city} onChange={set("city")} tid="checkout-city" />
            <Field label="Delivery Notes (optional)" v={form.notes} onChange={set("notes")} tid="checkout-notes" />
          </Section>

          <Section title="02 · Payment">
            <div className="space-y-3">
              <PayOpt
                sel={form.payment_method === "upi"}
                onClick={() => setForm({ ...form, payment_method: "upi" })}
                title="UPI Direct Pay"
                sub={`Scan QR or pay to ${upiId}. Instant. No card details needed.`}
                tid="pay-upi"
                highlight
              />
              <PayOpt
                sel={form.payment_method === "razorpay"}
                onClick={() => setForm({ ...form, payment_method: "razorpay" })}
                title="Razorpay · Cards, UPI, Netbanking"
                sub={razorpayEnabled ? "Secure payment via Razorpay." : "Not configured — add RAZORPAY keys to backend .env to enable."}
                tid="pay-razorpay"
                disabled={!razorpayEnabled}
              />
            </div>

            {form.payment_method === "upi" && (
              <UpiPayPanel
                upiId={upiId}
                businessName={businessName}
                amount={total}
                copied={copied}
                onCopy={() => { navigator.clipboard.writeText(upiId); setCopied(true); setTimeout(() => setCopied(false), 1600); }}
                reference={form.upi_reference}
                onReferenceChange={(v) => setForm({ ...form, upi_reference: v })}
              />
            )}
          </Section>

          {error && <div className="border border-[var(--accent)] text-[var(--accent)] p-4 label-mono" data-testid="checkout-error">{error}</div>}
        </div>

        <aside className="md:col-span-5 md:sticky md:top-24 h-fit border border-[var(--border)] bg-[var(--surface)] p-8">
          <div className="label-mono mb-6">Your Order</div>
          <div className="space-y-4 mb-6 max-h-72 overflow-y-auto">
            {items.map((it) => (
              <div key={it._uid} className="flex gap-3">
                <div className="w-14 h-14 shrink-0" style={{ background: "var(--surface-warm)" }}><img src={it.image_url} alt="" className="w-full h-full object-cover" /></div>
                <div className="flex-1">
                  <div className="font-serif text-base">{it.name}</div>
                  <div className="label-mono text-[9px]">{it.weight_g}g · {it.grind} · ×{it.quantity}</div>
                </div>
                <div className="font-mono text-sm text-[var(--primary)]">₹{it.price_inr * it.quantity}</div>
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-[var(--border)] space-y-2">
            <Row k="Subtotal" v={`₹${subtotal}`} />
            {discount > 0 && (
              <div className="flex justify-between label-mono" data-testid="checkout-discount-row">
                <span className="text-[var(--ember)]">{promoCode.toUpperCase()} · {discountPct}% off</span>
                <span className="font-mono text-[var(--ember)]">− ₹{discount}</span>
              </div>
            )}
            <Row k="Shipping" v={shipping === 0 ? "Free" : `₹${shipping}`} />
            <div className="flex justify-between pt-3 border-t border-[var(--border)] mt-2">
              <span className="label-mono text-[var(--text)]">Total</span>
              <span className="font-mono text-2xl text-[var(--primary-hover)]">₹{total}</span>
            </div>
          </div>
          <button type="submit" disabled={submitting} className="btn-primary w-full mt-6" data-testid="place-order-btn">
            {submitting ? "Placing…" : form.payment_method === "razorpay" ? "Pay Now" : form.payment_method === "upi" ? "Confirm & Place Order" : "Place Order"}
          </button>
        </aside>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-6 md:p-8 space-y-4">
      <div className="label-mono">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, v, onChange, required, type = "text", tid }) {
  return (
    <label className="block">
      <span className="label-mono block mb-2 text-[var(--text-muted)]">{label}{required && " *"}</span>
      <input className="input-dark" type={type} value={v} onChange={onChange} required={required} data-testid={tid} />
    </label>
  );
}
function PayOpt({ sel, onClick, title, sub, tid, disabled, highlight }) {
  return (
    <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled}
      className={`relative w-full p-4 border text-left transition-colors ${sel ? "border-[var(--primary)] bg-[var(--surface-2)]" : "border-[var(--border)]"} ${highlight ? "divine-pulse border-2" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      data-testid={tid}>
      {highlight && <span className="!absolute -top-3 right-4 btn-highlight !px-3 !py-1 !text-[10px]" data-testid={`${tid}-recommended-badge`}>Recommended · Fastest</span>}
      <div className="font-serif text-lg">{title}</div>
      <div className="label-mono text-[10px] mt-1">{sub}</div>
    </button>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between label-mono"><span>{k}</span><span className="font-mono text-[var(--text)]">{v}</span></div>;
}

function UpiPayPanel({ upiId, businessName, amount, copied, onCopy, reference, onReferenceChange }) {
  const upiUri = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(businessName)}&am=${amount}&cu=INR&tn=${encodeURIComponent("Rudra Roast order")}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=8&bgcolor=F6EFE1&color=3B2A1E&data=${encodeURIComponent(upiUri)}`;
  return (
    <div className="mt-6 border border-[var(--primary)] p-6" style={{ background: "var(--surface)" }} data-testid="upi-pay-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div className="flex justify-center">
          <div className="p-3 border border-[var(--border-strong)]" style={{ background: "var(--surface)", boxShadow: "var(--shadow-warm)" }}>
            <img src={qrSrc} alt="UPI QR" width={220} height={220} data-testid="upi-qr" />
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <div className="label-mono text-[var(--accent)]">Pay to</div>
            <div className="font-serif text-2xl mt-1">{businessName}</div>
          </div>
          <div>
            <div className="label-mono">UPI ID</div>
            <div className="flex items-center gap-2 mt-2">
              <code className="font-mono text-sm px-3 py-2 bg-[var(--bg-2)] border border-[var(--border)] flex-1 text-[var(--text)]" data-testid="upi-id">{upiId}</code>
              <button type="button" onClick={onCopy} className="btn-outline !py-2 !px-3 !text-[10px]" data-testid="upi-copy">
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
          <div>
            <div className="label-mono">Amount</div>
            <div className="font-mono text-3xl text-[var(--primary)] mt-1">₹{amount}</div>
          </div>
          <div className="text-[var(--text-secondary)] text-xs leading-relaxed">
            1. Open any UPI app (GPay, PhonePe, Paytm, BHIM). 2. Scan the QR or send to <span className="text-[var(--primary)]">{upiId}</span>. 3. Enter reference below.
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-[var(--border)]">
        <label className="block">
          <span className="label-mono block mb-2 text-[var(--primary)]">UPI Transaction Reference / UTR *</span>
          <input
            className="input-dark"
            placeholder="e.g. 4XXXXXXXX123 (12-digit UTR from your UPI app)"
            value={reference}
            onChange={(e) => onReferenceChange(e.target.value)}
            data-testid="upi-reference"
            required
          />
          <span className="label-mono text-[9px] mt-2 block text-[var(--text-muted)]">
            You&apos;ll see this in your UPI app after payment. We&apos;ll verify and confirm within a few hours.
          </span>
        </label>
      </div>
    </div>
  );
}
